<template>
    <div>
        <!-- Estado de carga -->
        <div v-if="loading" class="w-full">
            <!-- Título del skeleton -->
            <div v-if="title" class="mb-4">
                <h2 class="text-2xl font-bold">{{ title }}</h2>
                <p v-if="subtitle" class="text-gray-600 text-sm mb-2 md:mb-0">{{ subtitle }}</p>
            </div>
            
            <div class="flex gap-4 justify-center items-center py-8 w-full">
                <template v-for="n in skeletonCount" :key="n">
                    <div class="h-full w-full max-w-none bg-white rounded-xl shadow-md flex flex-col animate-pulse flex-1">
                        <div class="rounded-t-xl w-full h-[400px] sm:h-[600px] bg-gray-200"></div>
                        <div class="p-4 flex-1 flex flex-col">
                            <div class="h-4 w-2/3 bg-gray-200 rounded mb-2"></div>
                            <div class="h-3 w-full bg-gray-200 rounded mb-2"></div>
                            <div class="h-3 w-5/6 bg-gray-200 rounded mb-4"></div>
                            <div class="flex items-center mt-2">
                                <div class="rounded-full bg-gray-200 mr-2" style="width: 28px; height: 28px;"></div>
                                <div class="h-3 w-1/3 bg-gray-200 rounded"></div>
                                <div class="ml-auto h-3 w-12 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Estado de error -->
        <div v-else-if="error" class="flex justify-center items-center py-8">
            <div class="text-center">
                <div class="text-red-500 text-lg mb-2">⚠️</div>
                <p class="text-gray-600">Error al cargar los cuentos</p>
                <button @click="$emit('retry')" class="mt-2 text-blue-600 hover:text-blue-800">
                    Reintentar
                </button>
            </div>
        </div>

        <!-- Contenido principal -->
        <Splide v-else :options="{
            perPage: 3,
            gap: '0.5rem',
            pagination: false,
            arrows: true,
            breakpoints: {
                640: { perPage: 1 }
            }
        }">
            <SplideSlide v-for="story in stories" :key="story.id || story.title"
                class="flex justify-center items-center py-4">
                <div
                    class="h-full w-full min-w-[100%] max-w-[300px] bg-white rounded-xl shadow-md flex flex-col transition-transform duration-300 hover:scale-[1.01] hover:shadow-md cursor-pointer">
                    <div class="relative group">
                        <img
                            :src="getOptimizedImageUrl(story.image)"
                            alt="cover"
                            @click="$router.push(`/cuento/${story.documentId}`)"
                            class="rounded-t-xl h-[600px] sm:h-[600px] object-cover w-full cursor-pointer hover:opacity-90 transition-opacity"
                        />
                        <!-- Botón Escuchar solo en desktop, visible en hover SOLO sobre el afiche -->
                        <button
                            class="hidden md:flex items-center gap-2 px-4 py-2 bg-white text-black rounded absolute left-4 bottom-4 transition-all duration-500 opacity-0 translate-y-4 md:group-hover:opacity-100 md:group-hover:translate-y-0"
                            @click.stop="reproducirCuento(story)"
                            style="font-family: Inter; font-weight: 600; font-size: 16px; box-shadow: 0px 4px 4px 0px #00000040;"
                        >
                            Escuchar
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#222">
                                <polygon points="8,5 19,12 8,19" />
                            </svg>
                        </button>
                    </div>
                    <div class="p-4 flex-1 flex flex-col">
                        <!-- Slug del taller -->
                        <div v-if="story.taller?.slug" class="mb-1">
                            <span 
                              @click.stop.prevent="handleTallerClick(story.taller.slug)"
                              class="text-xs font-medium uppercase tracking-wide cursor-pointer hover:bg-[#D17A00] transition-colors"
                              style="font-family: Inter; font-weight: 500; font-size: 10px; line-height: 110%; letter-spacing: 0.5px; color: white; background-color: #E38400; padding: 2px 8px; border-radius: 12px;"
                              :title="`Filtrar por ${formatTallerSlug(story.taller.slug)}`"
                            >
                                {{ formatTallerSlug(story.taller.slug) }}
                            </span>
                        </div>
                        <!-- Descripción clickeable truncada -->
                        <router-link :to="`/cuento/${story.documentId}`"
                            class="mb-2 flex-1 text-sm text-gray-600 description-link line-clamp-2"
                            style="font-family: Inter; font-weight: 400; font-size: 14px; line-height: 130%; letter-spacing: 0%; color: #696969;"
                            :title="story.description || 'Sin descripción disponible'">
                            {{ story.description || 'Sin descripción disponible' }}
                        </router-link>
                        <!-- Tags -->
                        <div class="flex flex-wrap gap-1 mb-2">
                            <span 
                                v-for="(tag, index) in story.tags" 
                                :key="index" 
                                class="tag-carousel cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1"
                                @click.stop="navigateToFilterWithTag(tag)"
                                :title="`Filtrar por ${formatTags(tag)}`"
                            >
                                {{ formatTags(tag) }}
                            </span>
                        </div>
                        <div class="flex items-center mt-2 justify-between">
                            <!-- Autor: avatar e info -->
                            <div class="flex items-center">
                                <img
                                    v-if="story.alumno?.idUsuario && story.alumno?.imageLoaded !== false"
                                    :src="getAvatarUrl(story.alumno)"
                                    alt="Avatar del autor"
                                    class="rounded-full mr-2"
                                    style="width: 28px; height: 28px; object-fit: cover;"
                                    @error="handleImageError($event, story)"
                                    @load="handleImageLoad($event, story)"
                                />
                                <div
                                    v-if="story.alumno?.idUsuario && story.alumno?.imageLoaded === false"
                                    class="rounded-full mr-2 flex items-center justify-center text-xs font-medium text-white"
                                    style="width: 28px; height: 28px; background-color: #E38400;"
                                >
                                    {{ getInitials(story.alumno.nombre, story.alumno.apellido) }}
                                </div>
                                <span class="text-xs font-semibold"
                                    style="font-family: Inter; font-weight: 600; font-size: 12px; line-height: 110%; color: #000;">
                                    <span
                                        style="font-family: Inter; font-weight: 400; font-size: 12px; line-height: 110%; color: #000;">Por</span>
                                    {{ formatAuthorName(story.author) }}
                                </span>
                            </div>
                            <!-- Puntaje y estrella - solo mostrar si hay valoraciones -->
                            <div v-if="getStoryRating(story.documentId) !== '0.0'" class="flex items-center ml-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="black" viewBox="0 0 24 24" width="18"
                                    height="18" class="mr-1">
                                    <path
                                        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                </svg>
                                <span class="font-semibold text-base" style="font-size: 14px; color: #222;">
                                    {{ getStoryRating(story.documentId) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </SplideSlide>
        </Splide>
    </div>
</template>

<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useAudioPlayerStore } from '@/stores/audioPlayer';
import { useValoracionesStore } from '@/stores/valoraciones';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';


// Props del componente
const props = defineProps({
    stories: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    error: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    subtitle: {
        type: String,
        default: ''
    }
});

// Emitir eventos
const emit = defineEmits(['retry']);

// Store global de audio
const audioStore = useAudioPlayerStore();
const valoracionesStore = useValoracionesStore();
const authStore = useAuthStore();
const router = useRouter();

// Usar los datos de props o los datos por defecto
const stories = computed(() => {
    const storiesData = props.stories.length > 0 ? props.stories : [];
    return storiesData;
});

// Skeleton loader: 1 en mobile, 3 en desktop
const skeletonCount = ref(3);
function checkSkeletonCount() {
    skeletonCount.value = window.innerWidth < 640 ? 1 : 3;
}
onMounted(() => {
    checkSkeletonCount();
    window.addEventListener('resize', checkSkeletonCount);
});
onUnmounted(() => {
    window.removeEventListener('resize', checkSkeletonCount);
});

// Función para formatear nombres de autores (primera letra mayúscula, resto minúscula)
function formatAuthorName(name) {
    if (!name) return '';
    
    // Dividir el nombre en palabras
    const words = name.trim().split(' ').filter(word => word.length > 0);
    
    // Formatear cada palabra: primera letra mayúscula, resto minúscula
    const formattedWords = words.map(word => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
    
    return formattedWords.join(' ');
}

                // Función para formatear el slug del taller
                function formatTallerSlug(slug) {
                    if (!slug) return '';

                    // Mapeo de slugs a nombres más amigables (todo en minúsculas)
                    const mapeo = {
                        'taller-1': 'taller enero',
                        'taller-2': 'taller febrero',
                        'taller-mayo': 'taller mayo',
                        'taller-junio': 'taller junio',
                        'taller-julio': 'taller julio',
                        'taller-agosto': 'taller agosto',
                        'taller-septiembre': 'taller septiembre',
                        'taller-octubre': 'taller octubre',
                        'taller-noviembre': 'taller noviembre',
                        'taller-diciembre': 'taller diciembre',
                        'taller-enero': 'taller enero',
                        'taller-febrero': 'taller febrero',
                        'taller-marzo': 'taller marzo',
                        'taller-abril': 'taller abril'
                    };

                    // Si existe en el mapeo, usar el nombre formateado
                    if (mapeo[slug]) {
                        return mapeo[slug];
                    }

                    // Si no existe, formatear el slug: convertir guiones en espacios y todo en minúsculas
                    return slug
                        .replace(/-/g, ' ')
                        .toLowerCase();
                }



// Función para reproducir un cuento
const reproducirCuento = (story) => {
    audioStore.playCuento(story);
};

// Función para formatear tags (igual que en filtrar.vue)
const formatTags = (tag) => {
    if (!tag) return '';
    // Mapeo de tags para mostrar nombres más amigables
    const mapeo = {
        'nostalgico': 'Nostálgico',
        'agridulce': 'Agridulce',
        'inspirador': 'Inspirador',
        'intimista': 'Intimista',
        'ingenioso': 'Ingenioso',
        'suspenso': 'Suspenso',
        'optimista': 'Optimista',
        'divertido': 'Divertido',
        'melodramatico': 'Melodramático',
        'sentimental': 'Sentimental',
        'misterioso': 'Misterioso',
        'dramatico': 'Dramático',
        'tragico': 'Trágico',
        'poetico': 'Poético',
        'existencial': 'Existencial',
        'reflexivo': 'Reflexivo',
        'filosofico': 'Filosófico',
        'absurdo': 'Absurdo',
        'realista': 'Realista',
        'crudo': 'Crudo',
        'violento': 'Violento',
        'politico': 'Político',
        'social': 'Social',
        'familiar': 'Familiar',
        'adolescencia': 'Adolescencia',
        'vejez': 'Vejez',
        'infantil': 'Infantil',
        'fantastico': 'Fantástico',
        'magico': 'Mágico',
        'distopico': 'Distópico',
        'futurista': 'Futurista',
        'retro': 'Retro',
        'historico': 'Histórico',
        'costumbrista': 'Costumbrista',
        'urbano': 'Urbano',
        'rural': 'Rural',
        'epico': 'Épico',
        'intimo': 'Íntimo',
        'lgbtq': 'LGBTQ+',
        'feminista': 'Feminista',
        'espiritual': 'Espiritual',
        'religioso': 'Religioso',
        'experimental': 'Experimental',
        'minimalista': 'Minimalista',
        'grandilocuente': 'Grandilocuente',
        'onirico': 'Onírico',
        'argentinidad': 'Argentinidad',
        'de-epoca': 'De época',
        'autobiografico': 'Autobiográfico',
        'regional': 'Regional',
        'coral': 'Coral',
        'road-movie': 'Road movie',
        'psicologico': 'Psicológico',
        'de-aventuras': 'De aventuras',
        'de-investigacion': 'De investigación',
        'de-encierro': 'De encierro',
        'animalista': 'Animalista',
        'inclusivo': 'Inclusivo',
        'con-critica-social': 'Con crítica social',
        'viajes': 'Viajes'
    };
    return mapeo[tag.toLowerCase()] || tag;
};

// Función para obtener el value original del tag
const getOriginalTagValue = (formattedTag) => {
    // Mapeo inverso para obtener el value original
    const mapeoInverso = {
        'Nostálgico': 'nostalgico',
        'Agridulce': 'agridulce',
        'Inspirador': 'inspirador',
        'Intimista': 'intimista',
        'Ingenioso': 'ingenioso',
        'Suspenso': 'suspenso',
        'Optimista': 'optimista',
        'Divertido': 'divertido',
        'Melodramático': 'melodramatico',
        'Sentimental': 'sentimental',
        'Misterioso': 'misterioso',
        'Dramático': 'dramatico',
        'Trágico': 'tragico',
        'Poético': 'poetico',
        'Existencial': 'existencial',
        'Reflexivo': 'reflexivo',
        'Filosófico': 'filosofico',
        'Absurdo': 'absurdo',
        'Realista': 'realista',
        'Crudo': 'crudo',
        'Violento': 'violento',
        'Político': 'politico',
        'Social': 'social',
        'Familiar': 'familiar',
        'Adolescencia': 'adolescencia',
        'Vejez': 'vejez',
        'Infantil': 'infantil',
        'Fantástico': 'fantastico',
        'Mágico': 'magico',
        'Distópico': 'distopico',
        'Futurista': 'futurista',
        'Retro': 'retro',
        'Histórico': 'historico',
        'Costumbrista': 'costumbrista',
        'Urbano': 'urbano',
        'Rural': 'rural',
        'Épico': 'epico',
        'Íntimo': 'intimo',
        'LGBTQ+': 'lgbtq',
        'Feminista': 'feminista',
        'Espiritual': 'espiritual',
        'Religioso': 'religioso',
        'Experimental': 'experimental',
        'Minimalista': 'minimalista',
        'Grandilocuente': 'grandilocuente',
        'Onírico': 'onirico',
        'Argentinidad': 'argentinidad',
        'De época': 'de-epoca',
        'Autobiográfico': 'autobiografico',
        'Regional': 'regional',
        'Coral': 'coral',
        'Road movie': 'road-movie',
        'Psicológico': 'psicologico',
        'De aventuras': 'de-aventuras',
        'De investigación': 'de-investigacion',
        'De encierro': 'de-encierro',
        'Animalista': 'animalista',
        'Inclusivo': 'inclusivo',
        'Con crítica social': 'con-critica-social',
        'Viajes': 'viajes'
    };
    
    return mapeoInverso[formattedTag] || formattedTag.toLowerCase();
};

// Función para navegar a la página de filtros con un tag seleccionado
const navigateToFilterWithTag = (tag) => {
    // El tag ya es el value original, asegurar que esté en minúsculas
    const tagValue = tag.toLowerCase();
    console.log('🏷️ Navegando a filtros con tag:', { tagValue: tagValue });
    
    router.push({
        path: '/cuento/filtrar',
        query: { tags: tagValue }
    }).then(() => {
        console.log('✅ Navegación exitosa a filtros con tag:', tagValue);
    }).catch((error) => {
        console.error('❌ Error al navegar a filtros:', error);
    });
};

// Función para manejar clic en badge de taller
const handleTallerClick = (tallerSlug) => {
    console.log('🏫 Click en badge de taller:', { tallerSlug: tallerSlug });
    
    // Navegar a la página de filtros con el parámetro tallerSlug
    router.push({
        path: '/cuento/filtrar',
        query: { tallerSlug: tallerSlug }
    }).then(() => {
        console.log('✅ Navegación exitosa a filtros con tallerSlug:', tallerSlug);
    }).catch((error) => {
        console.error('❌ Error al navegar a filtros:', error);
    });
};


// Función para optimizar URLs de imágenes
const getOptimizedImageUrl = (imageUrl) => {
    if (!imageUrl) return '';

    // Si la URL ya tiene el prefijo small_, la devolvemos tal como está
    if (imageUrl.includes('/small_')) {
        return imageUrl;
    }

    // Si la URL contiene 'afiche_', agregamos el prefijo small_
    if (imageUrl.includes('afiche_')) {
        return imageUrl.replace('afiche_', 'small_afiche_');
    }

    // Si la URL es de cuentos (contiene /cuentos/ y cuento_), no modificamos
    if (imageUrl.includes('/cuentos/') && imageUrl.includes('cuento_')) {
        console.log('📸 Imagen de cuento detectada, usando original:', imageUrl);
        return imageUrl;
    }

    // Para otras URLs, intentamos agregar small_ antes del nombre del archivo
    try {
        const url = new URL(imageUrl);
        const pathParts = url.pathname.split('/');
        const fileName = pathParts[pathParts.length - 1];

        if (fileName && !fileName.startsWith('small_')) {
            pathParts[pathParts.length - 1] = `small_${fileName}`;
            url.pathname = pathParts.join('/');
            return url.toString();
        }
    } catch (e) {
        // Si no es una URL válida, devolvemos la original
        console.warn('URL de imagen no válida:', imageUrl);
    }

    return imageUrl;
};

// Función para obtener el rating de un cuento
const getStoryRating = (documentId) => {
    if (!documentId) return '0.0';
    
    const estadisticas = valoracionesStore.getEstadisticasByDocumentId(documentId);
    return estadisticas.promedio > 0 ? estadisticas.promedio.toFixed(1) : '0.0';
};

// Función para formatear el ID del usuario a 6 dígitos
const formatUserId = (id) => {
    const numericId = parseInt(id, 10);
    return String(numericId).padStart(6, "0");
};

// Función para obtener la URL del avatar del usuario
const getAvatarUrl = (alumno) => {
    if (!alumno?.idUsuario) return '';
    
    // Caso especial para Ignacio Champane (ID: 8699)
    if (alumno.idUsuario === "8699" || alumno.idUsuario === 8699) {
        return "https://files.hernancasciari.com/img/usuarios/020051.jpg";
    }
    
    // Para todos los demás usuarios, usar el formato estándar
    return `https://files.hernancasciari.com/img/usuarios/${formatUserId(alumno.idUsuario)}.jpg`;
};

// Función para obtener iniciales del nombre
const getInitials = (nombre, apellido) => {
    const firstInitial = nombre ? nombre.charAt(0).toUpperCase() : '';
    const lastInitial = apellido ? apellido.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
};

// Función para manejar errores de imagen
const handleImageError = (event, story) => {
    const img = event.target;
    const originalSrc = img.src;
    
    // Si es una imagen de usuario (contiene idUsuario)
    if (originalSrc.includes('files.hernancasciari.com/img/usuarios/')) {
        // Marcar que no se cargó para mostrar iniciales
        if (story && story.alumno) {
            story.alumno.imageLoaded = false;
        }
    } else {
        // Si es una imagen optimizada de cuento
        if (originalSrc.includes('small_')) {
            const originalUrl = originalSrc.replace('small_', '');
            img.src = originalUrl;
        } else {
            img.src = '/src/assets/images/Sonoro/anecdota.png';
        }
    }
};

// Función para manejar carga exitosa de imagen de usuario
const handleImageLoad = (event, story) => {
    if (story && story.alumno) {
        story.alumno.imageLoaded = true;
    }
};


</script>

<style scoped>
.tag-carousel {
    border: 1px solid #696969;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 11px;
    line-height: 110%;
    letter-spacing: 0%;
    text-align: center;
    color: #696969;
    background: #fff;
    border-radius: 999px;
    padding: 2px 10px;
    display: inline-block;
    transition: all 0.2s ease;
}

.tag-carousel:hover {
    background: #f3f4f6;
    border-color: #4b5563;
    color: #374151;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.description-link {
    transition: color 0.2s, text-decoration 0.2s;
    cursor: pointer;
}

.description-link:hover,
.title-link:hover {
    color: #222;
    text-decoration: none;
}

.title-link {
    transition: color 0.2s;
    text-decoration: none;
}

.title-link:hover {
    color: #222;
    text-decoration: none;
}

/* Estilos para truncado de líneas */
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>